/**
 * Create Activity Template - Form for creating new activity templates
 * Supports both platform and club-level activity creation
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AdminPageLayout, Button, FormInput, FormTextArea, FormSelect, Section, Card, Grid, Col, Badge } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const CreateActivityTemplate = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // Determine context from URL
  const location = useLocation();
  const currentPath = location.pathname;
  const { isPlatformAdmin, isClubAdmin, isBranchAdmin } = useMemo(() => ({
    isPlatformAdmin: currentPath.startsWith('/admin/system-templates'),
    isClubAdmin: currentPath.startsWith('/admin/templates'),
    isBranchAdmin: !!currentPath.match(/^\/branch\/[^/]+\/templates/)
  }), [currentPath]);
  const isCoach = currentPath.startsWith('/coach/templates');

  // Set back path
  let backPath;
  if (isPlatformAdmin) {
    backPath = '/admin/system-templates/activities';
  } else if (isClubAdmin) {
    backPath = '/admin/templates/activities';
  } else if (isBranchAdmin) {
    const branchId = currentPath.split('/')[2];
    backPath = `/branch/${branchId}/templates/activities`;
  } else if (isCoach) {
    backPath = '/coach/templates/activities';
  } else {
    backPath = '/admin/templates/activities';
  }

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    activity_type: '',
    default_duration_mins: 15,
    skill_id: '',
    is_active: true
  });
  const [availableSkills, setAvailableSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ACTIVITY_TYPES = [
    { value: 'warmup', label: 'Warmup' },
    { value: 'conditioning', label: 'Conditioning' },
    { value: 'cooldown', label: 'Cooldown' },
    { value: 'skill_building', label: 'Skill Building' },
    { value: 'scrimmage', label: 'Scrimmage' },
    { value: 'support', label: 'Support' }
  ];

  React.useEffect(() => {
    loadAvailableSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvailableSkills = async () => {
    try {
      setLoadingSkills(true);
      let skills = [];

      if (isPlatformAdmin) {
        // Platform admin loads all platform skill templates
        skills = await apiService.getSkillTemplates(true);
      } else if (isClubAdmin || isBranchAdmin || isCoach) {
        // Club admin, branch admin, and coach all load club-level skills
        skills = await apiService.listClubSkills(true);
      }

      setAvailableSkills(Array.isArray(skills) ? skills : []);
    } catch (err) {
      // Silently handle error - skills are optional
      console.error('Failed to load skills:', err);
      setAvailableSkills([]);
    } finally {
      setLoadingSkills(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'default_duration_mins' ? parseInt(value, 10) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Please provide an activity name');
      return;
    }

    if (!formData.activity_type) {
      showError('Please select an activity type');
      return;
    }

    if (formData.default_duration_mins < 1 || formData.default_duration_mins > 300) {
      showError('Duration must be between 1 and 300 minutes');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        activity_type: formData.activity_type,
        default_duration_mins: formData.default_duration_mins,
        skill_id: formData.skill_id || null,
      };

      if (isPlatformAdmin) {
        // Platform admin creating system template
        await apiService.post('/api/admin/activity-templates', payload);
        showSuccess('Activity template created successfully');
      } else if (isClubAdmin) {
        // Club admin creating club activity
        const userInfo = await apiService.getCurrentUser();
        const clubId = userInfo.club_id;
        await apiService.post(`/api/clubs/${clubId}/activity-templates`, payload);
        showSuccess('Club activity created successfully');
      } else if (isBranchAdmin) {
        // Branch admin creating branch activity
        const branchId = currentPath.split('/')[2];
        await apiService.post(`/api/branches/${branchId}/activity-templates`, payload);
        showSuccess('Branch activity created successfully');
      } else if (isCoach) {
        // Coach creating branch activity
        const userInfo = await apiService.getCurrentUser();
        const branchId = userInfo.location_id;
        await apiService.post(`/api/branches/${branchId}/activity-templates`, payload);
        showSuccess('Activity created successfully');
      }

      navigate(backPath);
    } catch (err) {
      showError(`Failed to create activity: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminPageLayout
      title="CREATE ACTIVITY TEMPLATE"
      subtitle={isPlatformAdmin ? "Create a new platform-level activity template" : "Create a new custom activity"}
      backPath={backPath}
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

          <Section title="Basic Information">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormInput
                  label="Activity Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Dynamic Warmup Routine"
                  required
                />

                <FormTextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the purpose and details of this activity..."
                  rows={4}
                />

                <Grid columns={2} gap="1.5rem">
                  <Col span={1}>
                    <FormSelect
                      label="Activity Type *"
                      name="activity_type"
                      value={formData.activity_type}
                      onChange={handleChange}
                      required
                      placeholder="Select Activity Type"
                      options={ACTIVITY_TYPES}
                    />
                  </Col>
                  <Col span={1}>
                    <FormInput
                      label="Default Duration (minutes) *"
                      name="default_duration_mins"
                      type="number"
                      value={formData.default_duration_mins}
                      onChange={handleChange}
                      min="1"
                      max="300"
                      required
                    />
                  </Col>
                </Grid>
              </div>
            </Card>
          </Section>

          <Section title="Skill Linkage (Optional)">
            <Card level={1}>
              <FormSelect
                label="Linked Skill"
                name="skill_id"
                value={formData.skill_id}
                onChange={handleChange}
                placeholder="Select a skill (optional)"
                options={availableSkills.map(skill => ({
                  value: skill.id,
                  label: skill.name
                }))}
                help="Link this activity to a specific skill for skill-building activities"
                disabled={loadingSkills}
              />
              {loadingSkills && (
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginTop: '0.5rem' }}>
                  Loading available skills...
                </p>
              )}
              {!loadingSkills && availableSkills.length === 0 && (
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginTop: '0.5rem' }}>
                  No skills available. Create skills first to link them to activities.
                </p>
              )}
            </Card>
          </Section>

          <Section title="Status">
            <Card level={1}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'var(--surface-lvl2)',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border-light)',
                  flex: 1
                }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label htmlFor="is_active" style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Active
                  </label>
                </div>
                {formData.is_active ? (
                  <Badge variant="success" size="sm">ACTIVE</Badge>
                ) : (
                  <Badge variant="warning" size="sm">INACTIVE</Badge>
                )}
              </div>
            </Card>
          </Section>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '1rem',
            padding: '2rem',
            background: 'var(--surface-lvl1)',
            borderRadius: '16px',
            border: '1px solid var(--color-border-light)'
          }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(backPath)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="save"
              loading={isSubmitting}
              disabled={!formData.name || !formData.activity_type}
            >
              Create Activity
            </Button>
          </div>
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default CreateActivityTemplate;
